import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../config';
import DeleteSearchModal from './DeleteSearchModal';
import ViewSearchModal from './ViewSearchModal';

export default function SavedSearchesPanel({
  onClose,
  fetchWithAuth,
  onApplySearch,
  setActiveTab
}) {
  const { t } = useTranslation();
  const [savedSearches, setSavedSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, search: null });
  const [viewModal, setViewModal] = useState({ isOpen: false, search: null });

  const fetchSavedSearches = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/saved-searches`);
      const data = await response.json();
      setSavedSearches(data);
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedSearches();
  }, []);

  const handleApplySearch = (search) => {
    setTimeout(() => {
      setActiveTab('filters');
    }, 100);
    onApplySearch(search);
    onClose();
  };

  const handleDeleteClick = (search) => {
    setDeleteModal({ isOpen: true, search });
  };

  const handleDeleteConfirm = async () => {
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/saved-searches/${deleteModal.search.id}`, {
        method: 'DELETE',
      });
      await fetchSavedSearches(); // Refresh the list
      setDeleteModal({ isOpen: false, search: null });
    } catch (error) {
      console.error('Error deleting saved search:', error);
    }
  };

  const handleViewClick = (search) => {
    setViewModal({ isOpen: true, search });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full mt-14">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {savedSearches.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-4">
          {t('No saved searches found')}
        </p>
      ) : (
        savedSearches.map((search) => (
          <div
            key={search.id}
            className="p-4 mx-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {search.name}
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleViewClick(search)}
                  className="p-1 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                  title={t('View search details')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteClick(search)}
                  className="p-1 text-red-600 hover:text-red-800 dark:text-red-500 dark:hover:text-red-400"
                  title={t('Delete search')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                <button
                  onClick={() => handleApplySearch(search)}
                  className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  {t('Apply')}
                </button>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {dayjs(search.createdat).format('L')}
            </p>
          </div>
        ))
      )}

      <DeleteSearchModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, search: null })}
        onConfirm={handleDeleteConfirm}
        searchName={deleteModal.search?.name}
        className="mx-4"
      />

      <ViewSearchModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, search: null })}
        search={viewModal.search}
        className="mx-4"
      />
    </div>
  );
}